import { Box, Modal, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import APButton from "../APButton";
import { useNetwork } from "../../providers/NetworkProvider";
import { FloIdle } from "../APAssetAtlas";
import { useVocabIntl } from "../../hooks/useVocabTranslation";

interface NoNetworkModalProps {
  isOpen: boolean,
}

export function NoNetworkModal({ isOpen }: NoNetworkModalProps) {
  const [waiting, setWaiting] = useState(false);
  const [allowRetest, setAllowRetest] = useState(false);
  const [reached, setReached] = useState(false);
  const [code, setCode] = useState("");
  const { validateNetwork } = useNetwork();
  const { APFormattedMessage, getFormattedString } = useVocabIntl()

  const checkNetwork = () => {
    if (!waiting) {
      setAllowRetest(false);
      setTimeout(() => setAllowRetest(true), 10000);
      setWaiting(true);
      validateNetwork().then(({ reached, code }) => {
        setReached(reached);
        setCode(code);
      }).finally(() => setWaiting(false))
    }
  }

  useEffect(() => {
    if (isOpen) {
      checkNetwork();
    }
  }, [isOpen]);

  const header = useMemo(() => {
    return (<APFormattedMessage id={"lost-network-heading"} />)
  }, [])

  const subHeader = useMemo(() => {
    return (<>
      <Text key={"no-network-subheader"} whiteSpace={"pre-line"}>
        {reached ?
          <>
            <APFormattedMessage id={"server-error-subheading"} />
            {code}
          </> :
          <APFormattedMessage id={"lost-network-subheading"} />
        }
      </Text>
    </>)
  }, [reached])

  const actions = useMemo(() => {
    if (reached) {
      return (
        <APButton variant={"raised"} disabled={!allowRetest} colorScheme={"primary"} size={"lg"} onClick={checkNetwork}>
          {allowRetest ?
            <APFormattedMessage id={"lost-network-check"} /> :
            <APFormattedMessage id={"lost-network-wait"} />
          }
        </APButton>
      )
    } else {
      return <Spinner color={"primary.fg"} size={"xl"} alignSelf={"center"} />
    }
  }, [reached, allowRetest])

  return <Modal
    closeOnOverlayClick={false}
    isOpen={isOpen}
    onClose={() => {}}
    variant={"splashModal"}
    size="sm">
    <ModalOverlay>
      <ModalContent>
        <ModalHeader bgColor={"lightBlue"} display={"flex"} borderTopRadius={"1.5rem"} gap={["0.25rem", "0.5rem"]}
                     alignItems={"center"} maxHeight={"80%"} minHeight={"80%"} flexDirection={"column"}>
          <Box textAlign={"center"} fontSize={["2xl", "3xl"]} fontWeight={"bold"}>
            {header}
          </Box>
          <Box textAlign={"center"} fontSize={["lg", "xl"]} lineHeight={"1.3"} fontWeight={"normal"}>
            {subHeader}
          </Box>
          <Spinner hidden={allowRetest || !reached} emptyColor={"secondary.fg"} color={"primary.fg"} size={"xl"}
                   alignSelf={"center"}
                   margin={"2rem"} />
          <FloIdle display={!allowRetest && reached ? "none" : "block"} preserveAspectRatio={"meet"}
                   style={{ height: "full", width: "auto", minHeight: "2rem", maxHeight: "8rem" }} />
        </ModalHeader>
        <ModalFooter display={"flex"} alignItems={"stretch"} flexDir={"column"} gap={"1rem"} maxHeight={"20%"}>
          {actions}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  </Modal>
}